/**
 * Clean up HTML on its way in from another program.
 *
 * Writers arrive with content in Word, Google Docs, Notion and Confluence, and
 * pasting is how it gets here. What those produce is HTML wrapped around a private
 * layout language — `class=MsoNormal`, `<o:p>`, `mso-list:l0 level1 lfo1`,
 * inline styles for every span — and none of it survives a Markdown save, so the
 * only question is whether it is dropped tidily or takes content with it.
 *
 * It took content. A table pasted from Word arrived as one EMPTY table per cell
 * with the cell text loose underneath it, because a table cell here holds inline
 * content — as a Markdown cell does — and Word puts a `<p>` inside every one.
 * ProseMirror cannot place a paragraph in an inline-only node, so it closed the
 * cell and lifted the paragraph out. A two-cell table came back as two empty
 * tables and two stray paragraphs.
 *
 * Done here rather than in the schema, because the schema is right: Markdown
 * table cells cannot hold blocks, and widening the node to accept a paste would
 * let the editor build documents the file format cannot express.
 */

/** Elements that carry no meaning this editor can keep. */
const DROP = ['script', 'style', 'meta', 'link', 'xml']

/** Block-level tags that must not survive inside a table cell. */
const BLOCKS = 'p, div, h1, h2, h3, h4, h5, h6, ul, ol, li, table, blockquote, pre'

export function cleanPastedHtml(html: string): string {
  // No document to parse against outside a browser — and nothing to clean, since
  // this only runs on a real paste.
  if (typeof DOMParser === 'undefined') return html

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const body = doc.body
  if (!body) return html

  for (const tag of DROP) for (const node of [...body.querySelectorAll(tag)]) node.remove()

  // Office namespace elements: `<o:p>`, `<w:sdt>`. They hold layout state and
  // occasionally a stray space, and a namespaced tag cannot match any parse rule.
  for (const node of [...body.querySelectorAll('*')]) {
    if (node.tagName.includes(':')) node.replaceWith(...node.childNodes)
  }

  // Comments, including Word's StartFragment/EndFragment markers.
  const walker = doc.createTreeWalker(body, 128 /* NodeFilter.SHOW_COMMENT */)
  const comments: Node[] = []
  while (walker.nextNode()) comments.push(walker.currentNode)
  for (const comment of comments) comment.parentNode?.removeChild(comment)

  /**
   * WebKit appends `<br class="Apple-interchange-newline">` to everything it
   * copies. Parsed as content it is a hard break — a lone `\` line in the
   * saved Markdown, on the end of everything pasted from a Mac.
   *
   * A real paste survives it today by luck rather than by rule: a break that
   * lands last in a SLICE is dropped when the slice is closed, which is the
   * path `prosemirror-view` takes for the clipboard. Nothing drops it when the
   * same fragment is parsed as a document, and nothing would drop it if the
   * artifact arrived anywhere but last. Both are one refactor away, and the
   * marker means "the copy ended here" in every case — so it goes.
   */
  for (const br of [...body.querySelectorAll('br.Apple-interchange-newline')]) br.remove()

  /**
   * A heading's weight is the heading's own styling, not a mark on its text.
   *
   * Notion's DOM puts `font-weight:600` on the heading ELEMENT itself (every
   * Notion heading is semibold), and a style rule reads that as bold — so a
   * pasted Notion heading arrived as `## **Publish the docs**`. Google avoids
   * this by accident: its heading spans say `font-weight:400`.
   */
  for (const heading of [...body.querySelectorAll('h1, h2, h3, h4, h5, h6')]) {
    ;(heading as HTMLElement).style.removeProperty('font-weight')
  }

  /**
   * A monospace span is inline code.
   *
   * Notion's block DOM has no `<code>` tag: inline code is a span styled with
   * a monospace font stack, a pink foreground and a grey background. Weight
   * and slant are read for us — ProseMirror's own style rules understand
   * `font-weight:700` (Google) and `font-weight:600` (Notion's semibold) and
   * `font-style:italic`, which is why bold and italic already survived — but
   * nothing reads a font stack, so code pasted from Notion arrived as plain
   * prose, `staleless publish` indistinguishable from the words around it.
   */
  for (const span of [...body.querySelectorAll('span')]) {
    const style = (span as HTMLElement).style
    if (!/monospace/i.test(style.fontFamily)) continue
    const code = doc.createElement('code')
    code.append(...span.childNodes)
    span.appendChild(code)
    style.removeProperty('font-family')
  }

  /**
   * Rejoin lists Notion split one item per list.
   *
   * A list item is a block in Notion, and its serialiser writes each block on
   * its own — so a three-bullet list arrives as three sibling one-item `<ul>`
   * elements (ordered lists as sibling `<ol start="1">`, `<ol start="2">`, …).
   * Pasted as-is that is three separate lists, and the Markdown serialiser
   * keeps them separate the only way Markdown can: alternating bullet markers
   * and a blank line between every item. Merging adjacent same-type siblings
   * loses nothing a Markdown document could express — adjacent separate lists
   * are not a thing the format has.
   */
  for (const list of [...body.querySelectorAll('ul, ol')]) {
    if (!list.isConnected) continue // already merged into an earlier sibling
    for (;;) {
      let next = list.nextSibling
      while (next && next.nodeType === 3 && (next.textContent ?? '').trim() === '') {
        const gap = next
        next = next.nextSibling
        gap.parentNode?.removeChild(gap)
      }
      if (!next || next.nodeType !== 1 || (next as Element).tagName !== list.tagName) break
      list.append(...(next as Element).childNodes)
      ;(next as Element).remove()
    }
  }

  /**
   * Flatten blocks inside a cell.
   *
   * Paragraphs are joined with a space rather than a line break: a Markdown
   * table cell is one line, so a break would either be dropped later or escape
   * the cell. The text is what the writer meant; the paragraph was Word's idea.
   */
  for (const cell of [...body.querySelectorAll('td, th')]) {
    if (!cell.querySelector(BLOCKS)) continue
    const parts: string[] = []
    for (const child of [...cell.children]) {
      const text = child.innerHTML.trim()
      if (text !== '') parts.push(text)
    }
    // Text sitting directly in the cell beside the blocks, which Word also does.
    const loose = [...cell.childNodes]
      .filter((n) => n.nodeType === 3)
      .map((n) => n.textContent?.trim() ?? '')
      .filter(Boolean)
    cell.innerHTML = [...loose, ...parts].join(' ')
  }

  // A paragraph holding nothing but a non-breaking space is how Word spells a
  // blank line. Kept, it becomes an empty paragraph in the document for every
  // one of them.
  for (const node of [...body.querySelectorAll('p, div')]) {
    if (node.querySelector('img, table, ul, ol')) continue
    if ((node.textContent ?? '').replace(/[\s ]+/g, '') === '') node.remove()
  }

  return body.innerHTML
}
